import React, { useRef, useState } from "react";
import uploadImage from "assets/images/upload-doc.svg";
import downloadIcon from "assets/icons/download-icon.svg";
import infoIcon from "assets/icons/info-icon.svg";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import cameraIcon from "assets/icons/camera-icon.svg";
import galleryIcon from "assets/icons/gallery-icon.svg";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import ImageCard from "./ImageCard";
import Button from "components/semantic/Button";
import Card from "components/common/Card";
import { saveDocList, uploadedFileType } from "store/slice/kyp.slice";
import { useAppDispatch } from "utils/hooks";

const UploadDoc = () => {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedDocs, setSelectedDocs] = useState<File[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const maxFiles = 10;
    const exceededFiles = files.filter((file) => file.size > maxSize);

    if (exceededFiles.length > 0) {
      toast.error("One or more files exceed the 10MB limit.");
      return;
    }
    if (files.length > maxFiles || selectedDocs.length === maxFiles) {
      toast.error(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    setSelectedDocs((prevDocs: any) => [...prevDocs, ...files]);
  };

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;

    const updatedDocs = Array.from(selectedDocs);

    const [movedDoc] = updatedDocs.splice(result.source.index, 1);
    updatedDocs.splice(result.destination.index, 0, movedDoc);

    setSelectedDocs(updatedDocs);
  };

  const handleRemoveImage = (index: number) => {
    const updatedDocs = [...selectedDocs];

    updatedDocs.splice(index, 1);

    setSelectedDocs(updatedDocs);
  };

  const submitHandler = () => {
    dispatch(saveDocList(selectedDocs));
    dispatch(uploadedFileType("image"));

    navigate("/kyp/upload-policy");
  };
  const handleCapture = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedDocs((prevDocs: any) => [...prevDocs, file]);
    }
  };

  return (
    <>
      {selectedDocs.length === 0 ? (
        <Card className="w-full">
          <div className=" flex my-20 flex-col gap-6 items-center justify-center">
            <div className="bg-[#F0F4FD] rounded-[10px] p-7">
              <img src={uploadImage} alt="upload-doc-image" />
            </div>
            <p className="font-medium text-center px-10">Upload images of your policy document to Preview and Submit</p>
            <button onClick={() => fileInputRef.current?.click()} className="bg-[#CEEADD] hidden md:flex mt-5 rounded-[6.75rem] gap-3  justify-center items-center w-56 py-3">
              <img src={downloadIcon} alt="download-icon" />
              <p className="font-bold">Upload</p>
              <input type="file" multiple accept={pageType === "image" ? ".jpg,.jpeg,.png" : ".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
            </button>
          </div>
        </Card>
      ) : (
        <>
          <div className="flex md:hidden gap-4 px-5 mb-3">
            <p className="font-bold text-[13px]">Click on submit button to upload the document.</p>
            <div className=" w-32">
              <Button title={"Submit"} onClick={submitHandler} className="block md:hidden" />
            </div>
          </div>
          <Card className="w-full" parentClassName="mb-10 md:mb-0">
            <div className="w-full pb-20  h-[32rem] overflow-scroll">
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="image-cards">
                  {(provided) => (
                    <div className="flex gap-3 flex-wrap pl-5 pt-5" ref={provided.innerRef} {...provided.droppableProps}>
                      {selectedDocs.map((img, index) => (
                        <Draggable key={index} draggableId={`image-${index}`} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <ImageCard count={index + 1} imgSrc={URL.createObjectURL(img)} removeImg={() => handleRemoveImage(index)} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>{" "}
            <div className="flex  bottom-0 z-10 w-full items-center gap-3 h-14 bg-[#F0F4FD] py-6 px-8">
              <img src={infoIcon} alt="info-icon" />
              <p>Press and hold to drag and drop the image to change sequence</p>
            </div>
            <div className="hidden md:flex justify-center gap-4 items-center  my-5">
              <div className="hidden md:flex justify-center">
                <button onClick={() => fileInputRef.current?.click()} className="bg-[#CEEADD] rounded-[6.75rem] gap-3 flex justify-center items-center w-56 py-3">
                  <img src={downloadIcon} alt="download-icon" />
                  <p className="font-bold">Upload</p>
                  <input type="file" multiple accept={pageType === "image" ? ".jpg,.jpeg,.png" : ".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                </button>
              </div>
              <Button title={"Submit"} onClick={submitHandler} className="hidden md:block" />
            </div>
          </Card>
        </>
      )}

      <nav className="fixed block md:hidden bottom-0 right-0 left-0 h-20  bg-white shadow-polifyx  lg:pt-4">
        <div className="flex justify-center items-center h-full gap-[1px] ">
          {pageType === "image" && (
            <label htmlFor="cameraInput" className="w-1/2 flex flex-col items-center justify-center bg-[#DAE4FA] py-4">
              <img src={cameraIcon} />
              <p>Camera</p>
              <input type="file" accept="image/*" capture="environment" id="cameraInput" className="hidden" onChange={handleCapture} />
            </label>
          )}
          <div onClick={() => fileInputRef.current?.click()} className={`${pageType === "image" ? "w-1/2" : "w-full"} flex flex-col items-center  justify-center bg-[#CEEADD]  py-4`}>
            <img src={galleryIcon} />
            <p>Photo Library</p>
          </div>
        </div>
      </nav>
    </>
  );
};

export default UploadDoc;
